/* -----------------Globals--------------- */
import { useState } from "react";

/* -----------------UI--------------- */
import BottomFab from "@memorang/ui/src/components/BottomFab";
import Box from "@memorang/ui/src/components/Box";
import Loader from "@memorang/ui/src/components/Loader";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import useBreakpoints from "@memorang/ui/src/useBreakpoints";

import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
import { useRouter } from "expo-router";
import { getDashboardData, getStats } from "../helpers/dashboard-data";
import useUserLeaderboard from "../hooks/useUserLeaderboard";

/* -----------------Types--------------- */
import type { MixedItemDist } from "@memorang/types/src/tag";

/* -----------------Child components--------------- */
import StudyPackView from "../components/StudyPackView";

import { events } from "@constants/tracking";
/* -----------------Analytics--------------- */
import { trackCustomEvent } from "analytics";

import { appBarMeta } from "@constants/appbar";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";

import useMixedItemDist from "@hooks/useMixedItemDist";
import CustomAppbar from "components/CustomAppbar";
import type { QuickActionsChipType } from "components/QuickActionChips/types";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PurchaseDialog } from "../components/PurchaseDialog";
import { getWeakTagsIds } from "../helpers/common";

const StudyPackContainer = () => {
	const { isMobile } = useDevice();
	const showStudyPackAsHome = useAppStore((store) => store.showStudyPackAsHome);

	const { data: mixedItemDistResponse } = useMixedItemDist(
		true,
		"GET_STUDYPACK_DISTRIBUTION",
	);

	const {
		currentExamContext: { examName },
	} = useExamContext();

	const { smDown } = useBreakpoints();
	const finalHeaderTitle = smDown ? examName : appBarMeta["product-home"].title;

	const finalSubtitle = smDown ? "" : appBarMeta["product-home"].description;

	const customProps = showStudyPackAsHome
		? {
				mode: isWeb ? ("medium" as const) : ("small" as const),
				align: "flex-start" as const,
			}
		: {};

	const CustomLoader = () => {
		return (
			<Box flex={1}>
				<CustomAppbar
					back={true}
					{...customProps}
					options={{
						headerTitle: finalHeaderTitle,
						subtitle: finalSubtitle,
						...(isMobile && { headerRight: () => null }),
					}}
					role="banner"
				/>
				<Loader />
			</Box>
		);
	};

	if (!mixedItemDistResponse) {
		return <CustomLoader />;
	}

	return (
		<>
			{mixedItemDistResponse && (
				<StudyPackRenderer
					mixedItemDistResponse={mixedItemDistResponse}
					headerTitle={finalHeaderTitle}
					headerSubtitle={finalSubtitle}
				/>
			)}
		</>
	);
};

export default StudyPackContainer;

const StudyPackRenderer = ({
	mixedItemDistResponse,
	headerTitle,
	headerSubtitle,
}: {
	mixedItemDistResponse: MixedItemDist;
	headerTitle: string;
	headerSubtitle: string | React.ReactNode;
}) => {
	const { isMobile, isMediumWindowSize } = useDevice();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const theme = useAppTheme();
	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const showStudyPackAsHome = useAppStore((store) => store.showStudyPackAsHome);
	const groupByCategory = useAppStore((store) => store.groupByCategory);

	const router = useRouter();
	const data = mixedItemDistResponse;

	const inset = useSafeAreaInsets();

	const handleNavigateToIAP = () => {
		trackCustomEvent({
			eventName: events.upgradeChipClicked,
		});
		if (isMobile || isMediumWindowSize) {
			router.push({
				pathname: "/purchase",
				params: {
					numItems,
					productType: "STUDY_PACK",
				},
			});
		} else {
			toggleShowPurchaseDialog();
		}
	};
	const {
		distribution,
		productImage,
		productTitle,
		sectionTags,
		numItems,
		masteryPercentage,
	} = getDashboardData(data, handleNavigateToIAP, groupByCategory);

	const weakTagsIds = getWeakTagsIds(data);

	const handleQuickActionPress = (action: QuickActionsChipType["type"]) => {
		trackCustomEvent({
			eventName: events.quickActionClicked,
			action,
		});
		switch (action) {
			case "weak":
				router.push({
					pathname: "/(protected)/session/customize",
					params: {
						selectedTagIds: weakTagsIds,
					},
				});
				break;
			case "leaderboard":
				router.push("/(protected)/(tabs)/leaderboard");
				break;
			case "progress":
				router.push("/(protected)/(tabs)/progress");
				break;
			default:
				break;
		}
	};

	//TODO:Below hooks look ugly to get just the leaderboard work, instead the api should return the stats

	const { userLeaderboardResponse } = useUserLeaderboard("all_time");

	const numAnswered = Object.entries(distribution).reduce(
		(acc, [key, value]) => (key !== "none" ? acc + value : acc),
		0,
	);

	const stats = getStats({
		masteryPercentage,
		numAnswered,
		xp: userLeaderboardResponse?.xp,
		isDarkMode: theme.dark,
	});

	const Wrapper = isWeb ? ScrollContainer : Box;

	const customProps = showStudyPackAsHome
		? {
				mode: isMobile ? ("small" as const) : ("medium" as const),
				align: "flex-start" as const,
			}
		: {};
	return (
		<>
			<CustomAppbar
				back={isWeb ? true : !isMobile}
				{...customProps}
				options={{
					headerTitle,
					subtitle: headerSubtitle,
				}}
				stats={stats}
				role="banner"
			/>
			<Wrapper
				style={{
					flexGrow: 1,
					paddingHorizontal: isMobile ? 0 : 16,
				}}
				// biome-ignore lint/a11y/useSemanticElements: <explanation>
				role="main"
			>
				<StudyPackView
					distribution={distribution}
					productTitle={productTitle}
					productImage={productImage}
					stats={stats}
					sectionTags={sectionTags}
					handleNavigateToIAP={handleNavigateToIAP}
					handleQuickActionPress={handleQuickActionPress}
				/>

				{isMobile && (
					<BottomFab
						icon={"play-outline"}
						bottom={inset.bottom}
						label="Quick Study"
						onPress={() => router.push("/(protected)/session/customize")}
					/>
				)}
			</Wrapper>

			{showPurchaseDialog ? (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType={"STUDY_PACK"}
				/>
			) : null}
		</>
	);
};
